/* Casamento de um valor cru de idioma (header `Accept-Language`, preferência
   gravada no cliente) contra os locales que o produto realmente tem.

   Retorna `null` quando nada casa — de propósito. Quem chama escreve o fallback
   (`matchLocale(x) ?? defaultLocale`), então o default aparece no diff em vez de
   ficar escondido aqui dentro. */

import { catalogs } from './catalogs.js';
import type { Locale } from './types.js';

const SUPPORTED_LOCALES = Object.keys(catalogs) as readonly Locale[];

/** `pt-BR;q=0.9` → `pt-br`. */
function normalizeTag(tag: string): string {
  const withoutQuality = tag.split(';')[0] ?? '';
  return withoutQuality.trim().toLowerCase();
}

function primarySubtag(tag: string): string {
  return tag.split('-')[0] ?? tag;
}

function matchSingleTag(tag: string): Locale | null {
  const normalized = normalizeTag(tag);
  if (normalized === '') {
    return null;
  }
  const exact = SUPPORTED_LOCALES.find((locale) => locale.toLowerCase() === normalized);
  if (exact !== undefined) {
    return exact;
  }
  /* `pt` casa com `pt-BR`: pedir a língua sem a região é o caso comum, e
     recusar por causa da região devolveria o idioma errado. */
  const primary = primarySubtag(normalized);
  return (
    SUPPORTED_LOCALES.find((locale) => primarySubtag(locale.toLowerCase()) === primary) ?? null
  );
}

/**
 * Resolve o primeiro locale suportado numa lista no formato `Accept-Language`
 * (`pt-BR,pt;q=0.9,en;q=0.8`) ou num valor único (`en-US`). A ordem do header é
 * respeitada como está: reordenar por `q` exigiria um parser completo de RFC
 * 9110 e, na prática, o cliente já manda em ordem de preferência.
 */
export function matchLocale(value: string | null | undefined): Locale | null {
  if (value === null || value === undefined) {
    return null;
  }
  for (const tag of value.split(',')) {
    const matched = matchSingleTag(tag);
    if (matched !== null) {
      return matched;
    }
  }
  return null;
}
